import { isSystemTest } from '@/core/utilities';
import { APPSERVER_API_CONTENT_TYPE } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { isDataLabAiAssistant } from '@/main/routing.utilities';
import type { MarkdownRenderingLocation } from '@/markdown/markdown.types';
import { aiAssistantEnabled, genAIAgents, genAIEnabled } from '@/services/systemConfiguration.utilities';
import HttpCodes from 'http-status-codes';
import type { DurationStore } from '@/trendData/duration.store';
import { getCsrfToken } from '@/utilities/auth.utilities';
import { generateRequestId, getOriginLabel, getOriginUrl } from '@/utilities/http.utilities';
import { isMobileDevice } from '@/utilities/utilities';
import { WORKSTEP_SCHEMA_VERSION } from '@/worksteps/worksteps.constant';
import type { WorkstepsStore } from '@/worksteps/worksteps.store';
import {
  endOfMonth,
  endOfWeek,
  isThisMonth,
  isThisWeek,
  isToday,
  isWithinInterval,
  isYesterday,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';
import type {
  Agent,
  AIMemory,
  AssistantOrigin,
  ChatContext,
  ChatData,
  ChatMessage,
  DocumentItem,
  DocumentsResponse,
  DocumentWithWorksheets,
  GroupedHistoryEntryType,
  HistoryEntryType,
  MemoriesResponse,
  SelectedDocument,
  ThinkingSection,
  ThinkingSectionItem,
  WorksheetsResponse,
} from './aiAssistant.types';
import { agents, getDataLabAiAgents } from './aiAssistant.types';
// Import the Seeq SDK components needed for the functions
import { ITEM_TYPES } from '@/homescreen/homescreen.constants';
import type { FolderOutputV1 } from '@/sdk/model/FolderOutputV1';
import type { IdentityPreviewV1 } from '@/sdk/model/IdentityPreviewV1';
import type { PermissionsV1 } from '@/sdk/model/PermissionsV1';
import type { WorksheetOutputV1 } from '@/sdk/model/WorksheetOutputV1';
import { FilterEnum, LocationsEnum, sqFoldersApi } from '@/sdk/api/FoldersApi';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { sqWorkbooksApi } from '@/sdk/api/WorkbooksApi';
import { logError } from '@/utilities/logger';
import { errorToast } from '@/utilities/toast.utilities';
import { EARLY_ACCESS_AGENTS } from '@/administration/administration.constants';
import { hasEarlyAccessCapability } from '@/services/authorization.service';
import type { VantageTableStore } from '@/vantage/vantageTable.store';

export function headers(origin?: MarkdownRenderingLocation | AssistantOrigin) {
  return {
    [SeeqNames.API.Headers.Csrf]: getCsrfToken(),
    [SeeqNames.API.Headers.RequestOrigin]: origin || SeeqNames.Origins.Chat,
    [SeeqNames.API.Headers.RequestOriginURL]: getOriginUrl(),
    [SeeqNames.API.Headers.RequestOriginLabel]: getOriginLabel(),
    [SeeqNames.API.Headers.RequestId]: generateRequestId(),
  };
}

/**
 * Gets the Chats for the current user.
 *
 * @returns  A chronologically ordered dictionary of chat conversations
 */
export async function getChats(): Promise<Record<string, HistoryEntryType>> {
  const response = await fetch('/genai/chats', { method: 'GET', headers: headers() });
  return await response.json();
}

/**
 * Gets the messages for a specific chat ID.
 *
 * @param chatId The ID of the chat to fetch messages for
 * @returns The chat data including messages and metadata
 */
export async function getChatMessages(chatId: string) {
  const response = await fetch(`/genai/chats/${chatId}`, { method: 'GET', headers: headers() });
  return (await response.json()) as ChatData;
}

export async function deleteChat(chatId: string): Promise<void> {
  await fetch(`/genai/chats?chat_id=${chatId}`, { method: 'DELETE', headers: headers() });
}

function getCapableAgents(): string {
  const agents = genAIAgents();
  if (!hasEarlyAccessCapability()) {
    return agents
      .split(',')
      .map((agent: string) => agent.trim())
      .filter(
        (agent: string) => !EARLY_ACCESS_AGENTS.some((expAgent) => expAgent.toLowerCase() === agent.toLowerCase()),
      )
      .join(',');
  }
  return agents;
}

export function getFriendlyMarkup(id: string) {
  const element = document.getElementById(id);
  if (!element) return '';

  const range = document.createRange();
  range.selectNodeContents(element);
  const selection = window.getSelection();
  if (!selection) return '';

  selection.removeAllRanges();
  selection.addRange(range);
  const text = selection.toString();
  selection.removeAllRanges();
  return text;
}

export function getAgents(): Agent[] {
  if (isDataLabAiAssistant()) {
    const availableAgents = getDataLabAiAgents();
    return agents.filter((agent) => availableAgents?.includes(agent.key));
  }
  const availableAgents = getCapableAgents()?.toLowerCase();
  return agents.filter((agent) => availableAgents?.includes(agent.key));
}

export function getAgentsForNewChat(): Agent[] {
  if (isDataLabAiAssistant()) {
    const availableAgents = getDataLabAiAgents();
    return agents.filter((agent) => availableAgents?.includes(agent.key));
  }
  const availableAgents = getCapableAgents()?.toLowerCase();
  return agents.filter((agent) => agent.key !== 'code' && availableAgents?.includes(agent.key));
}

export function getDefaultDisplayed(): boolean {
  return !isSystemTest() && !isMobileDevice() && aiAssistantEnabled() && genAIEnabled();
}

export const updateChat = async (chatId: string, { favorite, name }: { favorite?: boolean; name?: string }) => {
  return await fetch(`/genai/chats/${chatId}`, {
    method: 'PUT',
    headers: { ...headers(), 'content-type': APPSERVER_API_CONTENT_TYPE },
    body: JSON.stringify({
      favorite: favorite === undefined ? null : favorite,
      name: name === undefined ? null : name,
    }),
  });
};

export const groupChats = (chats: Record<string, HistoryEntryType>): GroupedHistoryEntryType => {
  const today = new Date();
  const groupedChats: GroupedHistoryEntryType = {
    favorites: [],
    shared_with_me: [],
    today: [],
    yesterday: [],
    this_week: [],
    last_week: [],
    this_month: [],
    last_month: [],
    older: [],
  };

  const determineCategory = (
    updatedAt: string | undefined | null,
    favorite: boolean,
    shared: boolean,
  ): keyof GroupedHistoryEntryType => {
    if (favorite) return 'favorites';
    if (shared) return 'shared_with_me';
    if (!updatedAt) return 'older';

    const updatedAtDate = new Date(updatedAt);
    if (isNaN(updatedAtDate.getTime())) return 'older';

    if (isToday(updatedAtDate)) return 'today';
    if (isYesterday(updatedAtDate)) return 'yesterday';
    if (isThisWeek(updatedAtDate, { weekStartsOn: 1 })) return 'this_week';

    const lastWeekStart = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
    const lastWeekEnd = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
    if (isWithinInterval(updatedAtDate, { start: lastWeekStart, end: lastWeekEnd })) return 'last_week';

    if (isThisMonth(updatedAtDate)) return 'this_month';

    const lastMonthStart = startOfMonth(subDays(today, 30));
    const lastMonthEnd = endOfMonth(subDays(today, 30));
    if (isWithinInterval(updatedAtDate, { start: lastMonthStart, end: lastMonthEnd })) return 'last_month';

    return 'older';
  };

  Object.values(chats).forEach((chat) => {
    const category = determineCategory(chat.updatedAt, chat.favorite, !!chat.sharedBy);

    groupedChats[category].push(chat);
  });

  return groupedChats;
};

export const getContext = (
  sqWorkstepsStore: WorkstepsStore,
  sqDurationStore: DurationStore,
  sqVantageTableStore: VantageTableStore,
  origin?: AssistantOrigin,
): ChatContext => {
  if (!sqWorkstepsStore?.current?.id) {
    return {};
  }

  const context: ChatContext = {
    workstep: { ...sqWorkstepsStore.current, version: WORKSTEP_SCHEMA_VERSION },
  };

  if (origin === 'vantage') {
    const { start, end } = sqDurationStore.investigateRange;
    context.vantage = {
      timeRange: `${start.toISOString(true)} - ${end.toISOString(true)}`,
      gridState: sqVantageTableStore?.gridState,
      selection: sqVantageTableStore?.selection ?? [],
      quickFilter: sqVantageTableStore?.quickFilter ?? '',
    };
  }

  return context;
};

/**
 * Gets the customer configuration from the GenAI config endpoint.
 * This configuration contains the corporate folder paths to match against Analysis Instructions.
 *
 * @returns Promise containing the customer configuration
 */
async function getCustomerConfigFolders(): Promise<string[] | null> {
  try {
    const response = await fetch('/genai/config/customer/folders', {
      method: 'GET',
      headers: headers(),
    });

    if (!response.ok) {
      console.warn('Failed to get customer config folders:', response.status);
      return null;
    }

    return (await response.json()) as string[];
  } catch (error) {
    logError('Error fetching customer config:', String(error));
    return null;
  }
}

/**
 * Searches for folders with name exactly matching "Analysis Instructions" using the items SDK.
 * Uses efficient item search API instead of recursive folder traversal.
 *
 * @returns Array of folder IDs that match "Analysis Instructions"
 */
async function findAnalysisInstructionsFolderIds(): Promise<string[]> {
  try {
    const { data } = await sqItemsApi.searchItems({
      filters: ['name==Analysis Instructions'],
      types: ['Folder'],
      offset: 0,
      limit: 20,
    });

    return data.items?.map((item) => item.id) || [];
  } catch (error) {
    logError('Error searching for Analysis Instructions folders:', String(error));
    return [];
  }
}

/**
 * Filters Analysis Instructions folders to only include those that match the configured corporate folder paths.
 * Converts the configFolderPaths array to a Set for O(1) lookup performance instead of O(n) array.includes().
 *
 * @param analysisInstructionsFolderIds Array of folder IDs for all "Analysis Instructions" folders
 * @param configFolderPaths Array of configured corporate folder paths to match against
 * @returns Array of folder IDs that match the configured paths
 */
async function filterAnalysisInstructionsByPath(
  analysisInstructionsFolderIds: string[],
  configFolderPaths: string[],
): Promise<string[]> {
  if (configFolderPaths.length === 0) {
    // If no paths configured, return empty array
    return [];
  }

  // Convert to Set for O(1) lookup performance instead of O(n) array.includes()
  const configPathsSet = new Set(configFolderPaths);
  const matchingFolderIds: string[] = [];

  // Check each Analysis Instructions folder against the configured paths
  for (const folderId of analysisInstructionsFolderIds) {
    try {
      // Get folder details including ancestors
      const { data: folderResponse } = await sqFoldersApi.getFolders({
        folderId,
        types: ['Topic'], // We're looking for Topics in these folders, so we use this API call
        limit: 1, // We just need the folder info, not the actual topics yet
      });

      // Build the path from ancestors (excluding Corporate)
      if (folderResponse.path && folderResponse.path.length > 0) {
        // Use the path property if available (this is the clean path without Corporate)
        const folderPath = folderResponse.path
          .filter((pathItem) => pathItem.name !== 'Analysis Instructions') // Remove Analysis Instructions itself
          .map((pathItem) => pathItem.name)
          .join(' >> ');

        // Check if this path matches any of the configured corporate folder paths using O(1) Set lookup
        if (configPathsSet.has(folderPath)) {
          matchingFolderIds.push(folderId);
        }
      }
    } catch (error) {
      logError(`Error checking path for Analysis Instructions folder ${folderId}:`, String(error));
    }
  }

  return matchingFolderIds;
}

/**
 * Gets all Topic documents within a specific folder using Corporate location filter.
 * This is used specifically for Analysis Instructions folders in the corporate hierarchy.
 *
 * @param folderId The ID of the folder to search in
 * @returns Array of Topic documents found in the folder
 */
async function getTopicsInAnalysisInstructionsFolder(folderId: string): Promise<FolderOutputV1[]> {
  try {
    const { data } = await sqFoldersApi.getFolders({
      folderId,
      types: ['Topic'],
      isArchived: false,
      limit: 50, // Reduced limit to prevent large responses
      sortOrder: 'updatedAt desc', // Sort by updatedAt in descending order
      locations: [LocationsEnum.Corporate], // Search in corporate location
    });

    return data.content || [];
  } catch (error) {
    logError(`Error getting topics in Analysis Instructions folder ${folderId}:`, String(error));
    return [];
  }
}

/**
 * Gets the available documents that can be referenced in the assistant.
 * For Topics, only includes those that are in an "Analysis Instructions" folder
 * within the Corporate hierarchy.
 *
 * @returns A list of documents that can be referenced
 */
export async function getDocuments(): Promise<DocumentsResponse> {
  try {
    // Get user's documents (Vantage)
    const { data: userVantageDocs } = await sqFoldersApi.getFolders({
      filter: FilterEnum.Mine,
      isArchived: false,
      sortOrder: 'updatedAt desc',
      offset: 0,
      limit: 20,
      onlyPinned: false,
      types: ['Vantage'],
      isExact: false,
      locations: [LocationsEnum.Mine],
    });

    // Get user's documents (Analysis)
    const { data: userAnalysisDocs } = await sqFoldersApi.getFolders({
      filter: FilterEnum.Mine,
      isArchived: false,
      sortOrder: 'updatedAt desc',
      offset: 0,
      limit: 20,
      onlyPinned: false,
      types: ['Analysis'],
      isExact: false,
      locations: [LocationsEnum.Mine],
    });

    // Get Topics from Analysis Instructions folders in Corporate hierarchy
    const topicsInAnalysisInstructions: FolderOutputV1[] = [];

    try {
      // Find all folders named "Analysis Instructions" using items search
      const allAnalysisInstructionsFolderIds = await findAnalysisInstructionsFolderIds();

      // Get corporate folder paths from customer config
      const configFolderPaths = await getCustomerConfigFolders();

      // Filter Analysis Instructions folders to only include those matching configured paths
      const matchingAnalysisInstructionsFolderIds = await filterAnalysisInstructionsByPath(
        allAnalysisInstructionsFolderIds,
        configFolderPaths || [],
      );

      // Get topics from all matching Analysis Instructions folders concurrently
      const topicPromises = matchingAnalysisInstructionsFolderIds.map(async (folderId) => {
        try {
          return await getTopicsInAnalysisInstructionsFolder(folderId);
        } catch (error) {
          console.warn(`Error getting topics from Analysis Instructions folder ${folderId}:`, error);
          return [];
        }
      });

      topicsInAnalysisInstructions.push(...(await Promise.all(topicPromises)).flat());
    } catch (error) {
      console.warn('Error getting topics from Analysis Instructions folders:', error);
      // Continue without Analysis Instructions topics
    }

    // Combine user documents with Analysis Instructions topics
    const allDocuments = [
      ...(userVantageDocs.content || []),
      ...(userAnalysisDocs.content || []),
      ...topicsInAnalysisInstructions,
    ];

    // Convert SDK response to expected DocumentsResponse format
    return {
      content: allDocuments.map(convertToDocumentItem),
      totalElements: allDocuments.length,
      totalPages: 1,
      size: allDocuments.length,
      number: 0,
    };
  } catch (error) {
    logError('Error in getDocuments:', String(error));
    // Return an empty response rather than throwing to prevent UI breakage
    return { content: [], totalElements: 0, totalPages: 0, size: 0, number: 0 };
  }
}

/**
 * Gets a specific document by ID.
 *
 * @param documentId The ID of the document to retrieve
 * @returns The document details or null if not found/error
 */
export async function getDocumentById(documentId: string): Promise<DocumentItem | null> {
  try {
    const { data } = await sqFoldersApi.getFolder({ folderId: documentId });
    return convertToDocumentItem(data);
  } catch (error) {
    logError(`Error in getDocumentById for ${documentId}:`, String(error));
    return null;
  }
}

/**
 * Gets a specific workbook by ID.
 * Used as a fallback if getDocumentById fails.
 *
 * @param workbookId The ID of the workbook to retrieve
 * @returns The workbook details or null if not found/error
 */
export async function getWorkbookById(workbookId: string): Promise<DocumentItem | null> {
  try {
    const { data } = await sqWorkbooksApi.getWorkbook({ id: workbookId });

    // Convert the workbook response to match DocumentItem format
    return {
      id: data.id,
      name: data.name,
      type: data.type,
      createdAt: data.createdAt || '',
      updatedAt: data.updatedAt || '',
      isPinned: false,
      owner: convertToOwner(data.owner),
      effectivePermissions: convertToPermissions(data.effectivePermissions),
      isArchived: data.isArchived || false,
    };
  } catch (error) {
    logError(`Error in getWorkbookById for ${workbookId}:`, String(error));
    return null;
  }
}

/**
 * Gets worksheets for a specific workbook/document.
 *
 * @param documentId The ID of the document to retrieve worksheets for
 * @param limit The maximum number of worksheets to return (default: 40)
 * @returns The worksheets or an empty response if not found/error
 */
export async function getWorksheets(documentId: string, limit = 40): Promise<WorksheetsResponse> {
  try {
    const { data } = await sqWorkbooksApi.getWorksheets({
      workbookId: documentId,
      offset: 0,
      limit,
      isArchived: false,
    });

    // Return SDK response in expected WorksheetsResponse format
    return {
      worksheets: data.worksheets || [],
      offset: data.offset || 0,
      limit: data.limit || 40,
    };
  } catch (error) {
    logError(`Error in getWorksheets for ${documentId}:`, String(error));
    // Return an empty response rather than throwing to prevent UI breakage
    return { worksheets: [], offset: 0, limit: 40 };
  }
}

/**
 * Formats a single document reference for inclusion in an AI prompt.
 *
 * @param document The document to reference
 * @returns A formatted string reference to the document
 */
function formatSingleDocumentReference(document: SelectedDocument): string {
  // If it's a worksheet, include that information
  if (document.worksheetId) {
    return `@${document.name} / ${document.worksheetName} (${document.id}/${document.worksheetId})`;
  }

  return `@${document.name} (${document.id})`;
}

/**
 * Extracts workbook and worksheet IDs from a URL.
 * Supports formats like:
 * - http://localhost:34216/workbook/WORKBOOK_ID/worksheet/WORKSHEET_ID
 * - http://localhost:34216/workbook/WORKBOOK_ID
 * - /workbook/WORKBOOK_ID/worksheet/WORKSHEET_ID
 * - /workbook/WORKBOOK_ID
 *
 * @param url The URL to parse
 * @returns Object containing extracted IDs, or null if no valid IDs found
 */
export function extractIdsFromUrl(url: string): { workbookId: string; worksheetId?: string } | null {
  try {
    if (!url) return null;

    // Define regex patterns for different URL formats
    // UUID format: 8-4-4-4-12 hexadecimal digits, sometimes with hyphens
    const uuidPattern = '[0-9A-F]{8}[-]?[0-9A-F]{4}[-]?[0-9A-F]{4}[-]?[0-9A-F]{4}[-]?[0-9A-F]{12}';

    // Pattern for full URL with both workbook and worksheet
    const fullPattern = new RegExp(`\\/workbook\\/(${uuidPattern})\\/worksheet\\/(${uuidPattern})`, 'i');
    const fullMatch = url.match(fullPattern);

    if (fullMatch && fullMatch.length >= 3) {
      return {
        workbookId: fullMatch[1],
        worksheetId: fullMatch[2],
      };
    }

    // Pattern for just a workbook ID
    const workbookPattern = new RegExp(`\\/workbook\\/(${uuidPattern})(?:\\/|$)`, 'i');
    const workbookMatch = url.match(workbookPattern);

    if (workbookMatch && workbookMatch.length >= 2) {
      return {
        workbookId: workbookMatch[1],
      };
    }

    return null;
  } catch (error) {
    logError('Error parsing URL:', String(error));
    return null;
  }
}

/**
 * Fetches a worksheet by its ID.
 *
 * @param workbookId The ID of the workbook containing the worksheet
 * @param worksheetId The ID of the worksheet to retrieve
 * @returns The worksheet details or null if not found/error
 */
export async function getWorksheetById(workbookId: string, worksheetId: string): Promise<WorksheetOutputV1 | null> {
  try {
    const { data } = await sqWorkbooksApi.getWorksheet({
      workbookId,
      worksheetId,
    });

    return data;
  } catch (error) {
    logError(`Error in getWorksheetById for ${worksheetId}:`, String(error));
    return null;
  }
}

/**
 * Helper function to convert SDK FolderOutputV1 to DocumentItem
 */
function convertToDocumentItem(folder: FolderOutputV1): DocumentItem {
  return {
    id: folder.id,
    name: folder.name,
    type: folder.type,
    createdAt: folder.createdAt || '',
    updatedAt: folder.updatedAt || '',
    isPinned: false, // FolderOutputV1 doesn't have isPinned, so default to false
    owner: convertToOwner(folder.owner),
    effectivePermissions: convertToPermissions(folder.effectivePermissions),
    isArchived: folder.isArchived || false,
  };
}

/**
 * Helper function to convert SDK IdentityPreviewV1 to the expected owner format
 */
function convertToOwner(owner?: IdentityPreviewV1): {
  id: string;
  name: string;
  type: string;
  username: string;
  email: string;
} {
  return {
    id: owner?.id || '',
    name: owner?.name || '',
    type: owner?.type || '',
    username: owner?.username || '',
    email: owner?.email || '',
  };
}

/**
 * Helper function to convert SDK PermissionsV1 to the expected permissions format
 */
export function convertToPermissions(permissions?: PermissionsV1): { read: boolean; write: boolean; manage: boolean } {
  return {
    read: permissions?.read || false,
    write: permissions?.write || false,
    manage: permissions?.manage || false,
  };
}

/**
 * Determines the current context/origin based on the pathname and workbook state.
 * This is shared between components to avoid duplication.
 *
 * @param pathname - The current pathname from useLocation()
 * @param isVantage - Whether the current workbook is a Vantage workbook
 * @param isTopic - Whether the current workbook is a Report Binder/Organizer workbook
 * @param returnFormat - Format of return value: 'origin' returns AssistantOrigin types, 'context' returns
 *   analysis/vantage/organizer/homescreen
 * @returns The current context/origin
 */
export function getCurrentContextOrigin(
  pathname: string,
  isVantage: boolean,
  isTopic: boolean,
  returnFormat?: 'origin',
): AssistantOrigin;
export function getCurrentContextOrigin(
  pathname: string,
  isVantage: boolean,
  isTopic: boolean,
  returnFormat: 'context',
): 'analysis' | 'vantage' | 'organizer' | 'homescreen';
export function getCurrentContextOrigin(
  pathname: string,
  isVantage: boolean,
  isTopic: boolean,
  returnFormat: 'origin' | 'context' = 'origin',
): AssistantOrigin | 'analysis' | 'homescreen' {
  // Check if we're in a workbook/worksheet context
  if (pathname.includes('/workbook/') || pathname.includes('/view/') || pathname.includes('/present/')) {
    if (isVantage) {
      return 'vantage';
    } else if (isTopic) {
      return 'organizer';
    } else {
      // Return different values based on format preference
      return returnFormat === 'context' ? 'analysis' : 'workbench';
    }
  } else {
    // For non-workbook contexts, check if we're in DataLab
    return isDataLabAiAssistant() ? 'datalab' : 'homescreen';
  }
}

/**
 * Normalizes selectedDocument to an array format.
 * This helper eliminates the repeated pattern of checking if selectedDocument is an array.
 *
 * @param selectedDocument - The selected document(s) which can be null, a single document, or an array
 * @returns An array of selected documents (empty array if input is null)
 */
export function normalizeSelectedDocuments(
  selectedDocument: SelectedDocument | SelectedDocument[] | null,
): SelectedDocument[] {
  if (!selectedDocument) {
    return [];
  }

  if (Array.isArray(selectedDocument)) {
    return selectedDocument;
  }

  return [selectedDocument];
}

/**
 * Gets the appropriate icon class for a document type.
 *
 * @param type - The document type
 * @returns The CSS class string for the document type icon
 */
export function getDocumentTypeIcon(type: string): string {
  switch (type) {
    case ITEM_TYPES.ANALYSIS:
      return 'fc fc-analysis';
    case ITEM_TYPES.ACTION_BOARD:
      return 'fa fa-sparkles';
    case ITEM_TYPES.TOPIC:
      return 'fc fc-report';
    case ITEM_TYPES.PROJECT:
      return 'fc fc-seeq-datalab';
    case ITEM_TYPES.FOLDER:
      return 'fa-folder-open';
    case ITEM_TYPES.JOURNAL:
      return 'fa-book';
    case ITEM_TYPES.REPORT:
      return 'fa-book';
    case ITEM_TYPES.VANTAGE:
      return 'fc-vantage';
    default:
      return 'fa-file-alt';
  }
}

/**
 * Gets the appropriate CSS class for a document type.
 *
 * @param type - The document type
 * @returns The CSS class string for the document type styling
 */
export function getDocumentTypeClass(type: string): string {
  const baseClasses = 'workbenchDisplay';

  switch (type) {
    case ITEM_TYPES.ANALYSIS:
      return baseClasses;
    case ITEM_TYPES.JOURNAL:
      return `${baseClasses} workbenchAnalysis`;
    case ITEM_TYPES.REPORT:
      return `${baseClasses} workbenchReport`;
    case ITEM_TYPES.TOPIC:
      return `${baseClasses} workbenchTopic`;
    case ITEM_TYPES.PROJECT:
      return `${baseClasses} workbenchProject`;
    case ITEM_TYPES.VANTAGE:
      return `${baseClasses} workbenchVantage`;
    case ITEM_TYPES.FOLDER:
      return `${baseClasses} sq-text-primary`;
    default:
      return baseClasses;
  }
}

/**
 * Gets the worksheet icon class based on the parent document type.
 *
 * @param parentDocumentType - The type of the parent document
 * @returns The CSS class string for the worksheet icon
 */
export function getWorksheetIconClass(parentDocumentType: string): string {
  return getDocumentTypeClass(parentDocumentType);
}

/**
 * Determines if the input text looks like a URL containing workbook/worksheet IDs.
 *
 * @param text - The text to check
 * @returns True if the text appears to be a workbook URL
 */
export function isWorkbookUrl(text: string): boolean {
  // Check if the text has the URL pattern we're looking for
  return (
    text.includes('/workbook/') &&
    /[0-9A-F]{8}[-]?[0-9A-F]{4}[-]?[0-9A-F]{4}[-]?[0-9A-F]{4}[-]?[0-9A-F]{12}/i.test(text)
  );
}

/**
 * Checks if any selected documents are Topic documents.
 *
 * @param selectedDocuments - Array of selected documents to check
 * @returns True if any document is of type Topic
 */
export function hasTopicDocuments(selectedDocuments: SelectedDocument[]): boolean {
  return selectedDocuments.some((doc) => doc.type === ITEM_TYPES.TOPIC);
}

/**
 * Checks if any selected documents are Analysis or Vantage documents.
 *
 * @param selectedDocuments - Array of selected documents to check
 * @returns True if any document is of type Analysis or Vantage
 */
export function hasAnalysisVantageDocuments(selectedDocuments: SelectedDocument[]): boolean {
  return selectedDocuments.some((doc) => doc.type === ITEM_TYPES.ANALYSIS || doc.type === ITEM_TYPES.VANTAGE);
}

/**
 * Determines which document should be used for context vs origin override
 * Priority Rules:
 * 1. If Topic documents exist: Topic for context, Analysis/Vantage for origin override
 * 2. If only Analysis/Vantage: Use the same document for both
 * 3. If only other types: Use the same document for both
 */
export function determineDocumentRoles(documents: SelectedDocument[]): {
  contextDocument: SelectedDocument[];
  originOverrideDocument: SelectedDocument;
} {
  // Separate documents by type
  const topicDocuments = documents.filter((doc) => doc.type === 'Topic');
  const analysisVantageDocuments = documents.filter((doc) => doc.type === 'Analysis' || doc.type === 'Vantage');
  const otherDocuments = documents.filter(
    (doc) => doc.type !== 'Topic' && doc.type !== 'Analysis' && doc.type !== 'Vantage',
  );

  // Rule 1: If Topic documents exist, use Topic for context
  if (topicDocuments.length > 0) {
    // Use Analysis/Vantage for origin override if available, otherwise first Topic
    const originOverrideDocument =
      analysisVantageDocuments.length > 0 ? analysisVantageDocuments[0] : topicDocuments[0];

    return { contextDocument: topicDocuments, originOverrideDocument };
  }

  // Rule 2: If only Analysis/Vantage, use the same document for both
  if (analysisVantageDocuments.length > 0) {
    return {
      contextDocument: analysisVantageDocuments,
      originOverrideDocument: analysisVantageDocuments[0],
    };
  }

  // Rule 3: Other documents
  return {
    contextDocument: otherDocuments,
    originOverrideDocument: otherDocuments[0],
  };
}

/**
 * Filters documents for Agent Q based on the current context.
 *
 * @param documents - The documents to filter
 * @param currentContext - The current context ('vantage', 'analysis', 'organizer', 'homescreen')
 * @returns Filtered documents based on Agent Q's context rules
 */
export function filterDocumentsForAgentQ(
  documents: DocumentWithWorksheets[],
  currentContext: 'analysis' | 'vantage' | 'organizer' | 'homescreen',
): DocumentWithWorksheets[] {
  if (currentContext === 'vantage' || currentContext === 'analysis') {
    // In vantage room or analysis room: only show topic workbooks
    return documents.filter((doc) => doc.type === ITEM_TYPES.TOPIC);
  } else if (currentContext === 'organizer') {
    // In organizer: can select vantage, analysis, or organizer topics
    return documents.filter(
      (doc) => doc.type === ITEM_TYPES.VANTAGE || doc.type === ITEM_TYPES.ANALYSIS || doc.type === ITEM_TYPES.TOPIC,
    );
  } else {
    // On homescreen or other contexts: show all documents (no filtering)
    return documents;
  }
}

/**
 * Filters documents based on a search query, checking both document names and worksheet names.
 * When worksheet names match but document name doesn't, only the matching worksheets are included.
 *
 * @param documents - The documents to filter
 * @param searchQuery - The search query to match against
 * @returns Object containing filtered documents, IDs of documents that should be auto-expanded, and worksheet
 *   filtering info
 */
export function filterDocumentsBySearch(
  documents: DocumentWithWorksheets[],
  searchQuery: string,
): {
  filteredDocuments: DocumentWithWorksheets[];
  documentsToExpand: string[];
  worksheetFilters: Record<string, string[]>; // documentId -> array of worksheet IDs to show
} {
  if (!searchQuery.trim()) {
    return {
      filteredDocuments: documents,
      documentsToExpand: [],
      worksheetFilters: {},
    };
  }

  const normalizedQuery = searchQuery.toLowerCase().trim();
  const filteredDocuments: DocumentWithWorksheets[] = [];
  const documentsToExpand: string[] = [];
  const worksheetFilters: Record<string, string[]> = {};

  documents.forEach((doc) => {
    // Check if document name matches
    const documentNameMatches = doc.name.toLowerCase().includes(normalizedQuery);

    // Find matching worksheets
    const matchingWorksheets =
      doc.worksheets?.filter((worksheet) => worksheet.name.toLowerCase().includes(normalizedQuery)) || [];

    const hasMatchingWorksheets = matchingWorksheets.length > 0;

    if (documentNameMatches || hasMatchingWorksheets) {
      if (documentNameMatches) {
        // Document name matches, include the whole document with all worksheets
        filteredDocuments.push(doc);
      } else if (hasMatchingWorksheets) {
        // Only worksheet names match, include original document but track filtered worksheets
        filteredDocuments.push(doc);

        // Track which worksheets should be shown for this document
        worksheetFilters[doc.id] = matchingWorksheets.map((w) => w.id);

        // Mark for expansion since we have matching worksheets
        documentsToExpand.push(doc.id);
      }
    }
  });

  return {
    filteredDocuments,
    documentsToExpand,
    worksheetFilters,
  };
}

/**
 * Remove a leading back-tick, single-quote, or double-quote that sits
 * **immediately in front of a high-risk shell command** (wget, curl, bash,
 * …). Doing so prevents Cloudflare's managed WAF rule 932100/932150
 * (“Unix Command Injection” in the OWASP Core Rule Set) from flagging the
 * request and returning **Error 1020 – Access denied**.
 *
 * References
 *   • Cloudflare doc – Error 1020:
 * https://developers.cloudflare.com/support/troubleshooting/http-status-codes/cloudflare-1xxx-errors/#error-1020-access-denied
 *   • OWASP CRS rule file – REQUEST-932-APPLICATION-ATTACK-RCE.conf:
 *     https://github.com/coreruleset/coreruleset/blob/v3.3/dev/rules/REQUEST-932-APPLICATION-ATTACK-RCE.conf
 */
export const stripQuotedCommand = (text: string) => {
  const cmds = [
    'wget',
    'curl',
    'bash',
    'sh',
    'python',
    'perl',
    'php',
    'nc',
    'netcat',
    'scp',
    'ftp',
    'tftp',
    'awk',
    'sed',
    'cat',
    'head',
    'tail',
    'tr',
    'chmod',
    'rm',
    'mv',
  ].join('|');

  const re = new RegExp(`([\\\`'"]|(?:\\\`{2,})|(?:'{2,})|(?:"{2,}))(\\s*)((?:${cmds})\\b[\\s\\S]*?)(?:\\1|$)`, 'gi');
  return text.replace(re, (_m, _q, ws, body) => ws + body);
};

/**
 * Calculate section end time as the sum of all item durations plus section start time
 */
export function calculateSectionEndTime(items: ThinkingSectionItem[], startTime: number): number {
  if (items.length === 0) {
    return Date.now();
  }

  // Sum the duration of all items that have both start and end times
  const totalItemDuration = items
    .filter((item) => item.startTime && item.endTime)
    .reduce((sum, item) => sum + (item.endTime! - item.startTime!), 0);

  return startTime + totalItemDuration;
}

/**
 * Fetches all user memories from the backend (both approved and pending)
 */
export async function getAllMemories(): Promise<MemoriesResponse> {
  try {
    const response = await fetch('/genai/user_memories', {
      method: 'GET',
      headers: headers(),
    });
    const memoriesData = (await response.json()) as MemoriesResponse;
    return memoriesData;
  } catch (error) {
    logError('Error fetching user memories:', String(error));
    return { approved: [], pending: [] };
  }
}

/**
 * Helper function to fetch memories by type (approved or pending)
 */
async function getMemoriesByType(type: 'approved' | 'pending'): Promise<AIMemory[]> {
  try {
    const memories = await getAllMemories();
    return memories[type];
  } catch (error) {
    logError(`Error fetching ${type} memories:`, String(error));
    return [];
  }
}

/**
 * Fetches only approved user memories (backward compatibility)
 */
export async function getUserMemories(): Promise<AIMemory[]> {
  return getMemoriesByType('approved');
}

/**
 * Fetches only pending user memories
 */
export async function getPendingMemories(): Promise<AIMemory[]> {
  return getMemoriesByType('pending');
}

/**
 * Deletes a specific approved user memory (for backward compatibility)
 */
export async function deleteUserMemory(memoryId: string): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/approved/${memoryId}`, {
      method: 'DELETE',
      headers: headers(),
    });

    if (!response.ok) {
      // For HTTP errors, use the generic delete error toast
      errorToast({
        messageKey: 'AI_ASSISTANT.ERROR.DISCARD_MEMORY',
      });
      return false;
    }

    return response.ok;
  } catch (error) {
    logError('Error deleting approved user memory:', String(error));
    return false;
  }
}

/**
 * Updates a specific user memory
 */
export async function updateUserMemory(memoryId: string, title: string, content: string): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/${memoryId}`, {
      method: 'PUT',
      headers: { ...headers(), 'content-type': APPSERVER_API_CONTENT_TYPE },
      body: JSON.stringify({
        title,
        content,
      }),
    });

    if (!response.ok) {
      // Handle 406 Not Acceptable errors for inappropriate content
      if (response.status === 406) {
        errorToast({
          messageKey: 'AI_ASSISTANT.ERROR.INAPPROPRIATE_CONTENT',
        });
        return false;
      }

      // Handle 507 Insufficient Storage errors with specific toast messages
      if (response.status === HttpCodes.INSUFFICIENT_STORAGE) {
        try {
          const errorData = await response.json();
          // The backend returns { detail: { detail: "message", error: "ERROR_CODE" } }
          const errorCode = errorData.detail?.error;
          if (errorCode === 'MEMORY_LIMIT_EXCEEDED') {
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.MEMORY_LIMIT_EXCEEDED',
            });
          } else if (errorCode === 'TOKEN_LIMIT_EXCEEDED') {
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.TOKEN_LIMIT_EXCEEDED',
            });
          } else {
            // Fallback for other 507 errors
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.UPDATE_MEMORY',
            });
          }
        } catch (parseError) {
          // If we can't parse the JSON, fall back to generic error
          errorToast({
            messageKey: 'AI_ASSISTANT.ERROR.UPDATE_MEMORY',
          });
        }
        return false;
      }

      // For other HTTP errors, use the generic error toast
      errorToast({
        messageKey: 'AI_ASSISTANT.ERROR.UPDATE_MEMORY',
      });
      return false;
    }

    return response.ok;
  } catch (error) {
    logError('Error updating user memory:', String(error));
    errorToast({
      messageKey: 'AI_ASSISTANT.ERROR.UPDATE_MEMORY',
    });
    return false;
  }
}

/**
 * Reverts a memory update by restoring the previous content
 */
export async function revertMemoryUpdate(
  memoryId: string,
  previousContent: string,
  previousTitle: string,
): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/${memoryId}`, {
      method: 'PUT',
      headers: { ...headers(), 'content-type': APPSERVER_API_CONTENT_TYPE },
      body: JSON.stringify({
        title: previousTitle,
        content: previousContent,
      }),
    });

    return response.ok;
  } catch (error) {
    logError('Error reverting memory update:', String(error));
    return false;
  }
}

/**
 * Approves a pending memory, moving it to the approved list
 */
export async function approvePendingMemory(memoryId: string): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/${memoryId}/approve`, {
      method: 'POST',
      headers: headers(),
    });

    if (!response.ok) {
      // Handle 507 Insufficient Storage errors with specific toast messages
      if (response.status === HttpCodes.INSUFFICIENT_STORAGE) {
        try {
          const errorData = await response.json();
          // The backend returns { detail: { detail: "message", error: "ERROR_CODE" } }
          const errorCode = errorData.detail?.error;
          if (errorCode === 'MEMORY_LIMIT_EXCEEDED') {
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.MEMORY_LIMIT_EXCEEDED',
            });
          } else if (errorCode === 'TOKEN_LIMIT_EXCEEDED') {
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.TOKEN_LIMIT_EXCEEDED',
            });
          } else {
            // Fallback for other 507 errors
            errorToast({
              messageKey: 'AI_ASSISTANT.ERROR.APPROVE_MEMORY',
            });
          }
        } catch (parseError) {
          // If we can't parse the JSON, fall back to generic error
          errorToast({
            messageKey: 'AI_ASSISTANT.ERROR.APPROVE_MEMORY',
          });
        }
        return false;
      }

      // For other HTTP errors, use the generic error toast
      errorToast({
        messageKey: 'AI_ASSISTANT.ERROR.APPROVE_MEMORY',
      });
      return false;
    }

    return response.ok;
  } catch (error) {
    logError('Error approving pending memory:', String(error));

    return false;
  }
}

/**
 * Deletes a pending memory
 */
export async function deletePendingMemory(memoryId: string): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/pending/${memoryId}`, {
      method: 'DELETE',
      headers: headers(),
    });
    if (!response.ok) {
      errorToast({
        messageKey: 'AI_ASSISTANT.ERROR.DISCARD_MEMORY',
      });
      return false;
    }
    return response.ok;
  } catch (error) {
    logError('Error deleting pending memory:', String(error));
    return false;
  }
}

/**
 * Deletes an approved memory
 */
export async function deleteApprovedMemory(memoryId: string): Promise<boolean> {
  try {
    const response = await fetch(`/genai/user_memories/approved/${memoryId}`, {
      method: 'DELETE',
      headers: headers(),
    });

    return response.ok;
  } catch (error) {
    logError('Error deleting approved memory:', String(error));
    return false;
  }
}

// Memory event system for cross-component communication
export type MemoryEvent = {
  type: 'approved' | 'rejected' | 'deleted';
  memoryId: string;
  timestamp: number;
};

class MemoryEventDispatcher {
  private listeners: ((event: MemoryEvent) => void)[] = [];

  subscribe(listener: (event: MemoryEvent) => void) {
    this.listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  dispatch(event: MemoryEvent) {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        logError('Error in memory event listener:', String(error));
      }
    });
  }

  // Convenience methods
  notifyApproved(memoryId: string) {
    this.dispatch({ type: 'approved', memoryId, timestamp: Date.now() });
  }

  notifyRejected(memoryId: string) {
    this.dispatch({ type: 'rejected', memoryId, timestamp: Date.now() });
  }

  notifyDeleted(memoryId: string) {
    this.dispatch({ type: 'deleted', memoryId, timestamp: Date.now() });
  }
}

// Global instance
export const memoryEventDispatcher = new MemoryEventDispatcher();

/**
 * Process messages when they are loaded from chat history.
 * This ensures thinking sections are properly processed for display and
 * dialog text is cleaned of any JSON thinking stage messages.
 */
export const processHistoryMessages = (messages: ChatMessage[]) => {
  return messages.map((message) => {
    if (message.role === 'bot' && message.thinkingSections && message.thinkingSections.length > 0) {
      const processedMessage = { ...message };
      processedMessage.thinkingSections = ensureThinkingSectionsStatus(message.thinkingSections);
      return processedMessage;
    }
    return message;
  });
};

/**
 * Helper function to process thinking sections from chat history.
 * Ensures that pre-loaded thinking sections have the correct status.
 */
export const ensureThinkingSectionsStatus = (sections: ThinkingSection[]): ThinkingSection[] => {
  return sections.map((section) => ({
    ...section,
    // Ensure all historical thinking sections are marked as error
    status: section.status === 'running' ? 'error' : section.status,
    // Process items to ensure they have proper status
    items: section.items.map((item) => ({
      ...item,
      // Ensure all historical items are marked as error unless they have error status
      status: item.status === 'running' ? 'error' : item.status,
    })),
  }));
};
